export type ContentLoadingSkeletonVariant =
  'compact' | 'inline' | 'chips' | 'panel' | 'cards' | 'list' | 'table' | 'form' | 'preview';

interface ContentLoadingSkeletonProps {
  label: string;
  variant?: ContentLoadingSkeletonVariant;
  rows?: number;
  className?: string;
}

function SkeletonLine({ className }: { className: string }) {
  return <div className={`skeleton-shimmer rounded-md ${className}`} />;
}

function CompactSkeleton() {
  return (
    <div className="flex w-full flex-col gap-3">
      <SkeletonLine className="h-5 w-40" />
      <SkeletonLine className="h-4 w-full max-w-xl" />
      <SkeletonLine className="h-4 w-3/4 max-w-lg" />
    </div>
  );
}

function InlineSkeleton() {
  return <div className="skeleton-shimmer h-4 w-3/4 max-w-sm rounded" />;
}

function ChipsSkeleton() {
  return (
    <div className="flex flex-wrap gap-2">
      <div className="skeleton-shimmer h-8 w-24 rounded-full" />
      <div className="skeleton-shimmer h-8 w-32 rounded-full" />
      <div className="skeleton-shimmer h-8 w-20 rounded-full" />
    </div>
  );
}

function PanelSkeleton() {
  return (
    <div className="flex w-full flex-col gap-4">
      <CompactSkeleton />
      <div className="grid gap-4 md:grid-cols-2">
        {Array.from({ length: 2 }, (_, index) => (
          <div key={index} className="rounded-xl border border-outline-default bg-raised p-5">
            <SkeletonLine className="mb-4 h-5 w-32" />
            <SkeletonLine className="mb-3 h-4 w-full" />
            <SkeletonLine className="h-10 w-full rounded-lg" />
          </div>
        ))}
      </div>
    </div>
  );
}

function CardsSkeleton({ rows }: { rows: number }) {
  return (
    <div className="grid w-full gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="rounded-xl border border-outline-default bg-raised p-5">
          <SkeletonLine className="mb-4 h-5 w-2/3" />
          <SkeletonLine className="mb-3 h-4 w-full" />
          <SkeletonLine className="mb-6 h-4 w-4/5" />
          <SkeletonLine className="h-10 w-full rounded-full" />
        </div>
      ))}
    </div>
  );
}

function ListSkeleton({ rows }: { rows: number }) {
  return (
    <div className="w-full overflow-hidden rounded-xl border border-outline-default bg-raised">
      {Array.from({ length: rows }, (_, index) => (
        <div
          key={index}
          className="flex items-center gap-3 border-b border-outline-default p-4 last:border-b-0"
        >
          <div className="skeleton-shimmer h-10 w-10 shrink-0 rounded-lg" />
          <div className="min-w-0 flex-1">
            <SkeletonLine className="mb-2 h-4 w-2/5" />
            <SkeletonLine className="h-3 w-4/5" />
          </div>
        </div>
      ))}
    </div>
  );
}

function TableSkeleton({ rows }: { rows: number }) {
  return (
    <div className="w-full overflow-hidden rounded-xl border border-outline-default bg-raised">
      <div className="grid grid-cols-3 gap-4 border-b border-outline-default bg-subtle p-4">
        <SkeletonLine className="h-3 w-24" />
        <SkeletonLine className="h-3 w-20" />
        <SkeletonLine className="ml-auto h-3 w-16" />
      </div>
      {Array.from({ length: rows }, (_, index) => (
        <div
          key={index}
          className="grid grid-cols-3 items-center gap-4 border-b border-outline-default p-4 last:border-b-0"
        >
          <SkeletonLine className="h-4 w-4/5" />
          <SkeletonLine className="h-4 w-3/5" />
          <SkeletonLine className="ml-auto h-8 w-20 rounded-full" />
        </div>
      ))}
    </div>
  );
}

function FormSkeleton() {
  return (
    <div className="grid w-full gap-5 rounded-xl border border-outline-default bg-raised p-5 sm:grid-cols-2">
      {Array.from({ length: 4 }, (_, index) => (
        <div key={index} className={index === 0 ? 'sm:col-span-2' : ''}>
          <SkeletonLine className="mb-2 h-3 w-28" />
          <SkeletonLine className="h-11 w-full rounded-lg" />
        </div>
      ))}
    </div>
  );
}

function PreviewSkeleton() {
  return (
    <div className="flex min-h-64 w-full flex-col justify-center rounded-xl border border-outline-default bg-primary p-5">
      <SkeletonLine className="mb-5 h-6 w-4/5" />
      <div className="grid grid-cols-2 gap-3">
        {Array.from({ length: 4 }, (_, index) => (
          <div key={index} className="skeleton-shimmer h-12 rounded-lg" />
        ))}
      </div>
    </div>
  );
}

export function ContentLoadingSkeleton({
  label,
  variant = 'panel',
  rows = 4,
  className = '',
}: ContentLoadingSkeletonProps) {
  const shape = (() => {
    switch (variant) {
      case 'compact':
        return <CompactSkeleton />;
      case 'inline':
        return <InlineSkeleton />;
      case 'chips':
        return <ChipsSkeleton />;
      case 'cards':
        return <CardsSkeleton rows={rows} />;
      case 'list':
        return <ListSkeleton rows={rows} />;
      case 'table':
        return <TableSkeleton rows={rows} />;
      case 'form':
        return <FormSkeleton />;
      case 'preview':
        return <PreviewSkeleton />;
      case 'panel':
        return <PanelSkeleton />;
    }
  })();

  return (
    <div role="status" aria-label={label} aria-busy="true" className={`w-full ${className}`.trim()}>
      <div aria-hidden="true" data-content-loading-shape={variant}>
        {shape}
      </div>
    </div>
  );
}
